import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Package } from "lucide-react";
import { adminClient, notifyProductsChanged } from "./api";
import ImageUploader from "./ImageUploader";
import VariantStockGrid from "./VariantStockGrid";
import MerchLayoutAdmin from "./MerchLayoutAdmin";
import ShippingCard from "./ShippingCard";
import BundlesAdmin from "./BundlesAdmin";
import PromoCodesAdmin from "./PromoCodesAdmin";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const EMPTY = {
  title: "", price: "", description: "", image: "",
  sizes: "S, M, L, XL", colors: "", stock: 10, is_active: true, sort_order: 0,
  extra_images: "", variant_image_map: "", department_id: "", trending: false, variant_stock: {}, sale_price: "", sale_starts_at: "", sale_ends_at: "",
};

export default function MerchAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("edit");
    if (!id || loading || !items.length) return;
    const it = items.find((x) => x.id === id);
    if (it) { openEdit(it); setParams({}, { replace: true }); setTimeout(() => document.querySelector('[data-testid="variant-stock-grid"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 400); }
  }, [params, loading, items]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: d }] = await Promise.all([adminClient.get("/admin/merch"), adminClient.get("/admin/merch/departments")]);
      setItems(data.items || []);
      setDepartments(d.items || []);
    } catch { toast.error("Could not load merch"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...item,
      sizes: (item.sizes || []).join(", "),
      colors: (item.colors || []).join(", "),
      extra_images: (item.images || []).join("\n"),
      variant_image_map: Object.entries(item.variant_images || {}).map(([c, u]) => `${c}=${u}`).join("\n"),
      department_id: item.department_id || "",
      trending: !!item.trending,
      variant_stock: item.variant_stock || {},
      sale_price: item.sale_price ?? "",
      sale_starts_at: item.sale_starts_at || "",
      sale_ends_at: item.sale_ends_at || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) return toast.error("Please upload a main image");
    const extraImages = String(form.extra_images || "").split("\n").map((s) => s.trim()).filter(Boolean);
    const variantImages = {};
    String(form.variant_image_map || "").split("\n").forEach((line) => {
      const [color, url] = line.split("=").map((s) => (s || "").trim());
      if (color && url) variantImages[color] = url;
    });
    const payload = {
      title: form.title,
      price: parseFloat(form.price) || 0,
      description: form.description || "",
      image: form.image,
      images: extraImages,
      variant_images: variantImages,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock, 10) || 0,
      is_active: !!form.is_active,
      sort_order: parseInt(form.sort_order, 10) || 0,
      department_id: form.department_id || null,
      trending: !!form.trending,
      variant_stock: form.variant_stock || {},
      sale_price: form.sale_price === "" || form.sale_price === null ? null : parseFloat(form.sale_price),
      sale_starts_at: form.sale_starts_at || null,
      sale_ends_at: form.sale_ends_at || null,
    };
    try {
      if (editing) {
        await adminClient.put(`/admin/merch/${editing.id}`, payload);
        toast.success("Updated · Live on site now", { icon: "🟢" });
      } else {
        await adminClient.post("/admin/merch", payload);
        toast.success("Product added · Live on site now", { icon: "🟢" });
      }
      notifyProductsChanged();
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await adminClient.delete(`/admin/merch/${item.id}`);
      toast.success("Deleted · Removed from site", { icon: "🟢" });
      notifyProductsChanged();
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (item) => {
    try {
      await adminClient.put(`/admin/merch/${item.id}`, {
        ...item,
        is_active: !item.is_active,
      });
      toast.success(item.is_active ? "Hidden from site" : "Now live on site", { icon: "🟢" });
      notifyProductsChanged();
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="merch-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Merch</h1>
          <p className="text-sm text-zinc-500">Products in your merch shop.</p>
        </div>
        <Button data-testid="merch-admin-add-btn" onClick={openCreate} className="btn-kinetic rounded-md text-xs">
          <Plus className="w-4 h-4 mr-1" /> New Product
        </Button>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="mb-4 bg-zinc-100">
          <TabsTrigger value="products" data-testid="merch-admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="layout" data-testid="merch-admin-tab-layout">Departments & Banners</TabsTrigger>
          <TabsTrigger value="bundles" data-testid="merch-admin-tab-bundles">Bundle deals</TabsTrigger>
          <TabsTrigger value="promos" data-testid="merch-admin-tab-promos">Promo codes</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="merch-admin-tab-shipping">Shipping</TabsTrigger>
        </TabsList>
        <TabsContent value="bundles"><BundlesAdmin products={items} /></TabsContent>
        <TabsContent value="promos"><PromoCodesAdmin /></TabsContent>
        <TabsContent value="layout"><MerchLayoutAdmin onChanged={load} /></TabsContent>
        <TabsContent value="shipping"><ShippingCard /></TabsContent>
        <TabsContent value="products">
      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <Package className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
          <p className="font-bold text-zinc-800">No products yet.</p>
          <Button onClick={openCreate} className="mt-4 btn-kinetic rounded-md text-xs">Add your first product</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`merch-admin-card-${it.id}`} className="overflow-hidden border-2 border-zinc-200">
              <div className="relative">
                <img src={it.image} alt={it.title} className="w-full h-40 object-cover" />
                {!it.is_active && <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-bold text-sm">HIDDEN</div>}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-display font-black text-[#0C2E2B]">{it.title}</h3>
                  <span className="font-mono-data font-black text-[#0C2E2B]">${it.price}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{it.description}</p>
                <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-zinc-600">
                  <span className="px-1.5 py-0.5 rounded bg-zinc-100 font-bold" data-testid={`merch-admin-dept-${it.id}`}>{departments.find((d) => d.id === it.department_id)?.name || "No department"}</span>
                  {it.trending && <span className="px-1.5 py-0.5 rounded bg-[#FFDF00] text-[#0C2E2B] font-bold">Trending</span>}
                  {it.sale_price ? <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold" data-testid={`merch-admin-sale-${it.id}`}>Sale ${Number(it.sale_price).toFixed(2)}{it.sale_ends_at ? ` → ${it.sale_ends_at}` : ""}</span> : null}
                  {it.variant_stock && Object.keys(it.variant_stock).length > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">Per-option stock</span>}
                  <span>Stock: <b>{it.stock}</b></span>
                  <span>· Sizes: {(it.sizes || []).join(", ") || "—"}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Switch data-testid={`merch-admin-toggle-${it.id}`} checked={it.is_active} onCheckedChange={() => toggleActive(it)} />
                    <span className="text-zinc-600">{it.is_active ? "Live" : "Hidden"}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button data-testid={`merch-admin-edit-${it.id}`} size="sm" variant="outline" className="rounded-md h-8" onClick={() => openEdit(it)}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`merch-admin-delete-${it.id}`} size="sm" variant="outline" className="rounded-md h-8 text-red-600 hover:text-red-700" onClick={() => remove(it)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit product" : "New product"}</DialogTitle>
            <DialogDescription>Changes go live on the site as soon as you save.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="merch-admin-form">
            <ImageUploader value={form.image} onChange={(u) => setForm({ ...form, image: u })} testId="merch-image-uploader" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Title</Label>
              <Input data-testid="merch-form-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Price ($)</Label>
                <Input data-testid="merch-form-price-input" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Stock</Label>
                <Input data-testid="merch-form-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Sizes (comma-separated)</Label>
              <Input data-testid="merch-form-sizes-input" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S, M, L, XL" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Colors (comma-separated)</Label>
              <Input data-testid="merch-form-colors-input" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Charcoal, Sand" className="mt-1" />
            </div>
            <div className="rounded-md border border-red-200 bg-red-50/40 p-3" data-testid="sale-pricing-block">
              <Label className="text-xs font-bold uppercase tracking-widest text-red-700">Sale pricing (optional)</Label>
              <p className="text-[10px] text-zinc-500 mb-2">Customers see the sale price with the regular price struck through. Leave dates blank for an open-ended sale.</p>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px] font-bold uppercase text-zinc-600">Sale price ($)</Label><Input data-testid="merch-form-sale-price-input" type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} className="mt-1 h-9" placeholder="e.g. 19.99" /></div>
                <div><Label className="text-[10px] font-bold uppercase text-zinc-600">Starts</Label><Input data-testid="merch-form-sale-start-input" type="date" value={form.sale_starts_at} onChange={(e) => setForm({ ...form, sale_starts_at: e.target.value })} className="mt-1 h-9" /></div>
                <div><Label className="text-[10px] font-bold uppercase text-zinc-600">Ends</Label><Input data-testid="merch-form-sale-end-input" type="date" value={form.sale_ends_at} onChange={(e) => setForm({ ...form, sale_ends_at: e.target.value })} className="mt-1 h-9" /></div>
              </div>
            </div>
            <VariantStockGrid sizes={form.sizes} colors={form.colors} value={form.variant_stock} defaultQty={parseInt(form.stock, 10) || 5} onChange={(v) => setForm({ ...form, variant_stock: v })} />
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Department</Label>
                <select data-testid="merch-form-department-select" value={form.department_id || ""} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                  <option value="">No department</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 h-10">
                <Switch data-testid="merch-form-trending-switch" checked={!!form.trending} onCheckedChange={(v) => setForm({ ...form, trending: v })} />
                <span className="text-sm">Show in "Latest Trending"</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Additional images (one URL per line)</Label>
              <Textarea data-testid="merch-form-extra-images-input" rows={3} value={form.extra_images} onChange={(e) => setForm({ ...form, extra_images: e.target.value })} placeholder="https://.../front.jpg&#10;https://.../back.jpg" className="mt-1 font-mono-data text-xs" />
              <p className="text-[10px] text-zinc-500 mt-1">Upload via admin/hero images or paste any image URL. These show as thumbnails on the product detail modal.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Color → image (one per line, format: Color=URL)</Label>
              <Textarea data-testid="merch-form-variant-images-input" rows={3} value={form.variant_image_map} onChange={(e) => setForm({ ...form, variant_image_map: e.target.value })} placeholder="Charcoal=https://.../charcoal.jpg&#10;Sand=https://.../sand.jpg" className="mt-1 font-mono-data text-xs" />
              <p className="text-[10px] text-zinc-500 mt-1">When a customer picks a color, the main image swaps to the matching URL.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Description</Label>
              <Textarea data-testid="merch-form-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Sort order</Label>
                <Input data-testid="merch-form-sort-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="merch-form-active-switch" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">{form.is_active ? "Live on site" : "Hidden"}</span>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="merch-form-submit-btn" type="submit" className="btn-kinetic rounded-md flex-1">
                {editing ? "Save changes" : "Add product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
